import { produce } from "immer";
import { assign, fromPromise, setup } from "xstate";

import { getDocumentDirPath } from "~/data/filesystem";
import { getDocsApiUrl } from "~/state/api/docsapi";
import { sqliteApi } from "~/state/api/sqlite";
import { tauriApi } from "~/state/api/tauri";

import { authenticationActorRef, waitForAccessTokenSilently } from "../authentication";

const cloneDocument = fromPromise<
	string,
	{ workspaceName: string; documentId: string; path?: string }
>(async ({ input }) => {
	const { workspaceName, documentId } = input;

	const path = input.path ?? (await getDocumentDirPath(documentId));
	const remoteUrl = `${getDocsApiUrl()}/git/${workspaceName}/${documentId}.git`;
	const token = await waitForAccessTokenSilently(authenticationActorRef);

	await tauriApi.clone({
		path,
		remoteUrl,
		token,
	});

	return path;
});

export const cloneDocumentMachine = setup({
	types: {
		context: {} as {
			workspaceName: string;
			documentId: string;
			path?: string;
		},
		input: {} as {
			workspaceName: string;
			documentId: string;
			path?: string;
		},
		output: {} as {
			path: string;
		},
	},
	actors: {
		cloneDocument,
		updateDocumentCache: fromPromise<void, { documentId: string; path: string }>(
			async ({ input }) => {
				await sqliteApi.documents.updatePath(input.documentId, input.path);
			}
		),
	},
}).createMachine({
	id: "clone-document",
	context: ({ input }) => ({
		workspaceName: input.workspaceName,
		documentId: input.documentId,
		path: input.path,
	}),
	initial: "cloningFromRemote",
	states: {
		cloningFromRemote: {
			invoke: {
				src: "cloneDocument",
				input: ({ context }) => ({
					workspaceName: context.workspaceName,
					documentId: context.documentId,
					path: context.path,
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Unable to clone document from remote", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					actions: assign(({ context, event }) =>
						produce(context, (draft) => {
							draft.path = event.output;
						})
					),
					target: "updatingDocumentCache",
				},
			},
		},
		updatingDocumentCache: {
			invoke: {
				src: "updateDocumentCache",
				input: ({ context }) => ({
					documentId: context.documentId,
					path: context.path!,
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Unable to update document cache", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "done",
				},
			},
		},
		done: {
			type: "final",
		},
	},
	output: ({ context }) => ({
		path: context.path!,
	}),
});
